import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-checkbox-field>` says about itself.
 *
 * THE LABEL IS CONTENT, NOT AN ATTRIBUTE — and this is the reason the examples below look the way
 * they do. `label` and `name` are both in `observedAttributes`, but `sync()` reads neither: the
 * label text comes from the DEFAULT SLOT, and there is no inner form control for a `name` to land
 * on. Setting `label="Send a receipt"` therefore draws a box with no words beside it, silently. The
 * old hand-kept example did exactly that, so the one page a person visited to learn how to use the
 * piece showed them the spelling that produces an unlabelled control. The examples here put the
 * text where the piece actually reads it, as a child. Neither attribute is listed as an input,
 * because an input table is a list of what setting a value DOES.
 *
 * THE BOX IS DRAWN, NOT NATIVE. It is a `<span role="checkbox" tabindex=0>` with a hand-built
 * glyph, so that the checked fill can read `--caos-color-accent` and repaint with a white-label
 * brand. Everything a native checkbox would give — Space and Enter, the focus ring, the mixed
 * state, clicking the label to toggle — is wired by hand to match; see `accessibility` below.
 *
 * INDETERMINATE RESOLVES TO CHECKED. First interaction clears `indeterminate` and sets `checked`,
 * which is what a native checkbox does. A page that means "some of these are on" has to set the
 * mixed state back itself after the group changes.
 */
const docs: ComponentDocs = {
  aliases: ['checkbox', 'tickbox', 'boolean field', 'toggle', 'yes/no field', 'flag'],

  answers: [
    'ask a yes-or-no question',
    'let somebody turn one option on or off',
    'show that some but not all of a group are selected',
    'record a flag on a record — tested, approved, taxable',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'checked',
      attribute: 'checked',
      property: true,
      type: 'boolean',
      description:
        'The box is ticked. It reflects — the piece writes it back on itself when the person toggles, so ' +
        'reading the attribute is reading the current state.',
      default: false,
    },
    {
      name: 'indeterminate',
      attribute: 'indeterminate',
      property: true,
      type: 'boolean',
      description:
        'The mixed state: a dash rather than a tick, announced as "mixed". For a box that stands for a group ' +
        'where only some are on. It is cleared on the first interaction and does not come back on its own.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not change this. Fades the row, refuses the toggle and takes the box out of the tab order.',
      default: false,
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'This box has to be ticked. Draws the asterisk after the label and marks the box required.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description:
        'The answer is wrong — usually a required box left unticked. Turns the box outline, its fill and the ' +
        'message red, and marks the box invalid for a screen reader.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the row, indented to sit under the label rather than under the box. Muted helper text ' +
        'on its own; the red reason when `invalid` is also set.',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant: a smaller box on its own, with the label and the message hidden ' +
        'because the surrounding record detail supplies the field name. Unlike the other fields, this one has ' +
        'no click-to-reveal step in that mode — the box is already its own affordance.',
      default: false,
    },
  ],

  // A plain DOM `change`, re-dispatched from the shadow root with `composed` so it crosses the
  // boundary. There is no `input` event here: the control has no intermediate state to report.
  events: [
    {
      name: 'change',
      when: 'When the box is toggled — by a click anywhere on the row, or Space or Enter on the box — and never when the page sets `checked` itself.',
      carries: 'nothing',
    },
  ],

  // Nothing to read back that cannot also be set. `checked` and `indeterminate` can be read off the
  // element, but a page can set both, so they are documented as inputs rather than here.
  state: [],

  slots: [{ name: '', takes: 'The label text beside the box. This is where the label goes; the attribute is not read.' }],

  tokens: [
    { token: '--caos-space-2', controls: 'The gap between the box and the label, and the indent of the message.' },
    { token: '--caos-space-1', controls: 'The gap above the message.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the box.' },
    { token: '--caos-color-border', controls: 'The outline of an unticked box.' },
    { token: '--caos-color-surface', controls: 'The fill of an unticked box.' },
    { token: '--caos-color-accent', controls: 'The outline of a box on hover and when it has keyboard focus.' },
    { token: '--caos-color-accent-fill', controls: 'The fill and outline of a ticked or mixed box.' },
    { token: '--caos-color-accent-contrast', controls: 'The tick and the dash drawn on that fill. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-color-danger-fill', controls: 'The fill of a ticked or mixed box that is invalid.' },
    { token: '--caos-color-danger-contrast', controls: 'The tick and the dash drawn on that invalid fill.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn around the box when it has keyboard focus.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the box and message when invalid.' },
    { token: '--caos-font', controls: 'The typeface of the label and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label.' },
    { token: '--caos-color-text', controls: 'The colour of the label.' },
    { token: '--caos-color-text-muted', controls: 'The colour of the message.' },
  ],

  parts: [
    { part: 'control', is: 'the clickable row, box and label together' },
    { part: 'box', is: 'the drawn box' },
    { part: 'label', is: 'the label beside the box' },
    { part: 'message', is: 'the line under the row' },
  ],

  methods: [
    {
      name: 'focus',
      description: 'Move keyboard focus to the box, so Space toggles it. The record-detail editor calls it when a value is clicked to edit.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section. It takes the full width of what holds it but draws only as wide as ' +
    'its own row, so several stacked in one section read as a group. It carries no group semantics of its ' +
    'own: a set of related boxes needs whatever holds them to say what they are a set OF.',

  whenToUse:
    'One independent yes-or-no answer, where both answers are ordinary. If ticking it commits something or ' +
    'takes effect immediately rather than being saved with the rest of the form, say so in the label.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws the right control, including a multi-select edited as a group of these.',
    },
    { name: 'SegmentedFilter', when: 'The choice changes what is shown rather than recording an answer.' },
    { name: 'PicklistField', when: 'There are more than a couple of options and only one may be chosen.' },
    { name: 'StatusPill', when: 'The yes-or-no is being reported rather than answered, and cannot be changed here.' },
  ],

  accessibility:
    'The box is a real focusable control with `role="checkbox"`, and everything a native one gives is wired to ' +
    'match: Space and Enter toggle it, clicking anywhere on the row toggles it, `aria-checked` reports true, ' +
    'false or mixed, `aria-required` and `aria-invalid` follow their attributes, and a disabled box reports ' +
    '`aria-disabled` and drops out of the tab order rather than staying focusable and inert. What is left to ' +
    'the author is the LABEL, which has to be the content — an empty element is an unnamed checkbox with ' +
    'nothing to announce — and, for a set of these, a name for the set on whatever holds them.',

  examples: [
    {
      key: 'on-off',
      label: 'Ticked and unticked',
      summary: 'The two ordinary states, with the label written where the piece reads it — as the content, not as an attribute.',
      placement: [
        { component: 'caos-checkbox-field', inputs: { checked: true }, children: ['Send a receipt'] },
        { component: 'caos-checkbox-field', children: ['Notify the account owner'] },
      ],
    },
    {
      key: 'mixed',
      label: 'Mixed',
      summary:
        'A box standing for a group where only some are on: a dash instead of a tick, announced as mixed. It ' +
        'looks like the ticked state and means something different, which is why it is its own example.',
      placement: { component: 'caos-checkbox-field', inputs: { indeterminate: true }, children: ['All line items received'] },
    },
    {
      key: 'refused',
      label: 'Required and refused',
      summary: 'A box that has to be ticked and has not been — the asterisk, the red box, and the reason underneath.',
      placement: {
        component: 'caos-checkbox-field',
        inputs: { required: true, invalid: true, message: 'Approval is required before this order can be submitted.' },
        children: ['Manager approval'],
      },
    },
  ],
};

export default docs;
